/**
 * Config-driven para el bloque "Admin (CxC)" / Finanzas (Fase 11, ver PLAN.md).
 * Archivo PROPIO (no se toca `config/resources.js` — regla de la tarea, otros
 * agentes trabajan en paralelo sobre ese archivo). Mismo shape que `RESOURCES` de
 * `resources.js`, consumido por el `ResourcePage` genérico.
 *
 * Fuente de verdad de campos: `IbuBack2/src/api/v1/modules/finanzas-*` (validators
 * reales), contrastado contra `IbuFront/src/components/views/Admin/*` (V1, solo para
 * UX/columnas). Ver DUDAS.md D-11.1..D-11.6 para las decisiones donde V1 y V2
 * divergen o donde V1 no tenía pantalla de referencia.
 *
 * Anticipos y MovimientosBancos NO viven aquí — son páginas bespoke
 * (`src/pages/finanzas/AnticiposPage.jsx`, `MovimientosBancosPage.jsx`) porque el
 * ResourcePage genérico + FormModal no dan para comisiones condicionales ni para un
 * listado de solo lectura con filtros por rutas (no query params).
 */
import apiClient from '../services/api/apiClient.js';
import Swal from 'sweetalert2';
import EstadoCuentaBancoButton from '../pages/finanzas/EstadoCuentaBancoButton.jsx';
import TraspasoBancoButton from '../pages/finanzas/TraspasoBancoButton.jsx';

const MONEDA_OPTS = [
  { value: 'MXN', label: 'MXN' },
  { value: 'USD', label: 'USD' },
];

const money = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

/** Botón "Pagar" (Cargos/CargosContenedor) — PATCH /{route}/{id}/pagar {saldo: monto}
 * (el nombre `saldo` en el body es el MONTO ABONADO, no el saldo resultante — así lo
 * documenta el propio validator del backend, nombre heredado de V1). Ver DUDAS.md D-11.1. */
function botonPagar(route) {
  return function ExtraPagar(row, { recargar }) {
    if (!(Number(row.saldo) > 0)) return null;
    return (
      <button
        key="pagar"
        className="text-success-600 text-xs font-medium"
        onClick={async () => {
          const { value: monto } = await Swal.fire({
            title: 'Registrar pago',
            input: 'number',
            inputLabel: 'Importe abonado',
            inputValue: row.saldo,
            showCancelButton: true,
            confirmButtonText: 'Pagar',
            cancelButtonText: 'Cancelar',
            inputValidator: (v) => (!v || Number(v) <= 0 ? 'Captura un importe válido' : undefined),
          });
          if (!monto) return;
          try {
            await apiClient.patch(`/${route}/${row._id}/pagar`, { saldo: Number(monto) });
            await recargar();
            Swal.fire({ icon: 'success', title: 'Pago registrado', timer: 1200, showConfirmButton: false });
          } catch (e) {
            Swal.fire({ icon: 'error', title: 'No se pudo registrar el pago', text: e.message });
          }
        }}
      >
        Pagar
      </button>
    );
  };
}

// Guard "no editar Cargo/CargoContenedor ya abonado" (segunda ronda de la auditoría
// CxC, 2026-09-28 — ver DUDAS.md, sección "Auditoría vista-por-vista Finanzas CxC",
// gap #1). Confirmado línea por línea contra V1:
//   - `ListadoCargos.jsx` L700: el botón "Editar" solo se muestra si
//     `c.saldo == (c.total + c.comision)` — es decir, mientras el Cargo NO tenga
//     ningún abono aplicado (si tuviera abonos, `saldo` sería menor a
//     `total + comisionNumero`). El candado adicional por fecha (`poderEditar`) que
//     V1 calculaba quedó SIEMPRE hardcodeado a `"Si"` en el objeto de la fila (línea
//     138), así que en la práctica nunca bloqueaba nada — no se replica ese candado
//     de fecha, solo el de abonos, que es el único realmente activo.
//   - `ListadoCargosContenedor.jsx` L695: mismo criterio pero sin comisión —
//     `c.saldo == c.total` (CargosContenedor no tiene campo de comisión). Ahí el
//     candado de fecha está comentado explícitamente (`// && c.poderEditar == "Si"`).
//
// IMPLEMENTACIÓN: se comparó DIRECTO contra `saldo == total (+ comisionNumero)`
// (fórmula literal de V1) y se probó contra `ibuPruebas` real -- 21 de 2322 Cargos
// activos "fallan" esa comparación exacta con `abonos:0` (verificado), por deriva de
// punto flotante HISTÓRICA ya presente en el dato (V1 usaba `$inc` sobre `Double`,
// ver `core/utils/money.js`): diferencias de hasta 1 centavo entre `saldo` y
// `total+comisionNumero` en Cargos que NUNCA tuvieron un abono. Comparar por
// centavos redondeados (`money.aCentavos`) no alcanza a cubrir el caso de 1 centavo
// completo de diferencia. Por eso la implementación real usa `abonos === 0`
// (campo propio, entero/exacto, sin la deriva de `saldo`) en vez de la fórmula
// literal de V1 -- MISMA intención de negocio ("¿ya se le aplicó algún abono a este
// Cargo?"), sin el falso-bloqueo de Cargos legítimamente sin abonar que el dato
// heredado de V1 produciría con una comparación literal de `saldo`.
// Se exporta como función pura para que la usen TANTO `cfg.puedeEditarFila` (guard
// genérico de `ResourcePage`, ver abajo) COMO las páginas bespoke
// `CargosPage.jsx`/`CargosContenedorPage.jsx` (que ya no usan `ResourcePage` para su
// formulario por la calculadora ligada — ver notas de `cargos`/`cargos-contenedor`
// más abajo) — una sola fórmula, sin duplicar la regla de negocio.
export function puedeEditarCargo(row) {
  return Number(row?.abonos || 0) === 0;
}
export function puedeEditarCargoContenedor(row) {
  return Number(row?.abonos || 0) === 0;
}

export const RESOURCES_FINANZAS_CXC = {
  cargos: {
    route: 'cargos', title: 'Cargo', recurso: 'cargos', sortBy: 'createdAt', exportable: true,
    // Guard "no editar si ya tiene abonos" (ver `puedeEditarCargo` arriba). Esta
    // entrada de config ya NO se usa para el formulario (`CargosPage.jsx` se volvió
    // bespoke por la calculadora %/$ comisión y USD/tipoCambio — ver nota de
    // `cargos-contenedor` más abajo, mismo motivo) pero se deja configurada aquí por
    // si algún día se vuelve a usar `ResourcePage` para esta ruta.
    puedeEditarFila: puedeEditarCargo,
    // BUG REAL corregido (auditoría 2026-09-28, mismo patrón encontrado y ya
    // corregido en `cargos-contenedor` — ver D-5.5/D-AUDIT-CONT.5): editar vía
    // `PATCH /cargos/:id` (`editar()`) NUNCA reajusta `Clientes.cargos/saldo`. V1
    // (`ListadoCargos.jsx`) siempre editaba vía `.../CargosEditImporte/:id`. Con
    // `editSuffix` se enruta a `/cargos/:id/importe` (`editarImporte()`), que sí
    // reajusta el acumulado del cliente por la diferencia. El validator
    // `cargos.validator.js#importeBody` se amplió para aceptar también
    // fecha/descripcion/conceptosGastos/moneda.
    editSuffix: '/importe',
    // Columnas "Numero"/"Comisión" agregadas (auditoría CxC 2026-09-28): V1
    // (`ListadoCargos.jsx`) siempre mostraba el folio `idCargo` (col. "Numero") y el
    // importe de comisión por fila — V2 los tenía capturables en el form pero
    // nunca los mostraba en la tabla.
    columns: [
      { key: 'idCargo', label: 'Número', sortable: true, render: (r) => r.idCargo ?? '—' },
      { key: 'fecha', label: 'Fecha', sortable: true },
      { key: 'clientes', label: 'Cliente', render: (r) => r.clientes?.nombre_comercial || r.clientes?.[0]?.nombre_comercial || '—' },
      { key: 'conceptosGastos', label: 'Concepto', render: (r) => r.conceptosGastos?.name || r.conceptosGastos?.[0]?.name || '—' },
      { key: 'descripcion', label: 'Descripción', sortable: true },
      { key: 'total', label: 'Importe', className: 'text-right', render: (r) => money(r.total) },
      { key: 'comisionNumero', label: 'Comisión', className: 'text-right', render: (r) => money(r.comisionNumero) },
    ],
    fields: [
      { key: 'fecha', label: 'Fecha', type: 'date', required: true },
      { key: 'clientes', label: 'Cliente', type: 'ref', refRoute: 'clientes', refLabel: 'nombre_comercial', required: true },
      { key: 'conceptosGastos', label: 'Concepto de gasto', type: 'ref', refRoute: 'conceptos-gastos', refLabel: 'name', required: true },
      { key: 'descripcion', label: 'Descripción', type: 'text', required: true },
      { key: 'moneda', label: 'Moneda', type: 'select', options: MONEDA_OPTS },
      { key: 'tipoCambio', label: 'Tipo de cambio', type: 'number', required: true },
      { key: 'total', label: 'Importe', type: 'number', required: true },
      { key: 'totalUSD', label: 'Importe USD', type: 'number', required: true },
      { key: 'comisionPorcentaje', label: 'Comisión %', type: 'number' },
      { key: 'comisionNumero', label: 'Comisión $', type: 'number' },
    ],
    // abonos/saldo son requeridos por el backend pero se calculan aquí, no los
    // captura el usuario (en alta abonos siempre es 0, ver D-11.1).
    // BUG REAL corregido (segunda ronda de auditoría 2026-09-28): `saldo` se
    // calculaba como `total - abonos`, IGNORANDO `comisionNumero` -- a diferencia de
    // V1 (`CargosCreate.jsx`/`ListadoCargos.jsx#editCargo`), que siempre guarda
    // `saldo = total + comisionNumero` (el cliente debe el importe MÁS la comisión).
    // Con el bug, un Cargo recién creado con comisión > 0 nacía con
    // `saldo !== total + comisionNumero`, disparando el guard `puedeEditarCargo` de
    // arriba como si YA tuviera abonos aplicados -- bloqueaba la edición de
    // cualquier Cargo con comisión desde el momento de crearlo.
    transformBeforeSave: (v, { editando }) => {
      const total = Number(v.total) || 0;
      const comisionNumero = Number(v.comisionNumero) || 0;
      const abonos = editando ? Number(editando.abonos) || 0 : 0;
      return {
        ...v,
        tipoCambio: Number(v.tipoCambio) || 1,
        total,
        totalUSD: Number(v.totalUSD) || total,
        comisionPorcentaje: Number(v.comisionPorcentaje) || 0,
        comisionNumero,
        abonos,
        saldo: total + comisionNumero - abonos,
      };
    },
    extraRowActions: botonPagar('cargos'),
  },

  // NOTA (auditoría Contenedores 2026-09-28, tarea "Calculadora ligada USD/MXN",
  // ver reporte): esta entrada YA NO se usa — `CargosContenedorPage.jsx`
  // (`src/pages/finanzas/CargosContenedorPage.jsx`) dejó de ser un `ResourcePage`
  // genérico y ahora es bespoke (mismo patrón que
  // `finanzas-cxp/GastosContenedorPage.jsx`), porque el `ResourcePage`/`FormModal`
  // genérico no soporta la calculadora ligada totalUSD<->tipoCambio<->total que V1
  // sí tiene (`ListadoCargosContenedor.jsx`). Se deja esta config sin borrar (por si
  // algún otro flujo la referenciara) pero no debería usarse para esa ruta.
  'cargos-contenedor': {
    route: 'cargos-contenedor', title: 'Cargo a Contenedor', recurso: 'cargos', sortBy: 'createdAt', exportable: true,
    // BUG REAL corregido (auditoría Contenedores 2026-09-28, ver reporte): el
    // `ResourcePage` genérico editaba vía PATCH `/cargos-contenedor/:id` (método
    // `editar()`), que NUNCA reajusta `Clientes.cargos/saldo` ni
    // `Contenedores.totalCargos` — a diferencia de V1
    // (`ListadoCargosContenedor.jsx#editCargo`), que SIEMPRE editaba vía
    // `PATCH .../cargosContenedorEditImporte/:id`. Con `editSuffix` se enruta el
    // PATCH de edición a `/cargos-contenedor/:id/importe` (`editarImporte()`), que sí
    // reajusta ambos acumulados por la diferencia — igual que V1. El validator
    // `cargos-contenedor.validator.js#importeBody` se amplió para aceptar también
    // fecha/descripcion/conceptosGastos/moneda (antes solo saldo/total/totalUSD/
    // tipoCambio/clientes/contenedores), para no perder esos campos silenciosamente
    // (`stripUnknown:true`).
    editSuffix: '/importe',
    // Guard "no editar si ya tiene abonos" (ver `puedeEditarCargoContenedor`
    // arriba) — igual que `cargos`, esta entrada ya no se usa para el formulario
    // (bespoke, ver nota arriba) pero se deja configurada por consistencia.
    puedeEditarFila: puedeEditarCargoContenedor,
    columns: [
      { key: 'fecha', label: 'Fecha', sortable: true },
      { key: 'contenedores', label: 'Contenedor', render: (r) => r.contenedores?.numeroInterno || r.contenedores?.[0]?.numeroInterno || '—' },
      { key: 'clientes', label: 'Cliente', render: (r) => r.clientes?.nombre_comercial || r.clientes?.[0]?.nombre_comercial || '—' },
      { key: 'conceptosGastos', label: 'Concepto', render: (r) => r.conceptosGastos?.name || r.conceptosGastos?.[0]?.name || '—' },
      { key: 'descripcion', label: 'Descripción', sortable: true },
      { key: 'total', label: 'Importe', className: 'text-right', render: (r) => money(r.total) },
    ],
    fields: [
      { key: 'fecha', label: 'Fecha', type: 'date', required: true },
      { key: 'clientes', label: 'Cliente', type: 'ref', refRoute: 'clientes', refLabel: 'nombre_comercial', required: true },
      // D-11.2 CORREGIDA (auditoría Contenedores 2026-09-28): confirmado contra
      // `IbuBack2/.../contenedores.model.js` que el campo real es `numeroInterno`
      // (V1 también mostraba `numeroInterno`, nunca `numero`) — corregido aquí y en
      // la columna de la tabla arriba.
      { key: 'contenedores', label: 'Contenedor', type: 'ref', refRoute: 'contenedores', refLabel: 'numeroInterno', required: true },
      { key: 'conceptosGastos', label: 'Concepto de gasto', type: 'ref', refRoute: 'conceptos-gastos', refLabel: 'name', required: true },
      { key: 'descripcion', label: 'Descripción', type: 'text', required: true },
      { key: 'moneda', label: 'Moneda', type: 'select', options: MONEDA_OPTS },
      { key: 'tipoCambio', label: 'Tipo de cambio', type: 'number', required: true },
      { key: 'total', label: 'Importe', type: 'number', required: true },
      { key: 'totalUSD', label: 'Importe USD', type: 'number', required: true },
    ],
    transformBeforeSave: (v, { editando }) => {
      const total = Number(v.total) || 0;
      const abonos = editando ? Number(editando.abonos) || 0 : 0;
      return {
        ...v,
        tipoCambio: Number(v.tipoCambio) || 1,
        total,
        totalUSD: Number(v.totalUSD) || total,
        abonos,
        saldo: total - abonos,
      };
    },
    extraRowActions: botonPagar('cargos-contenedor'),
  },

  abonos: {
    route: 'abonos', title: 'Abono', recurso: 'abonos', sortBy: 'createdAt', exportable: true,
    // Filtros de Fecha Inicio/Fin y Cliente (segunda ronda de auditoría CxC,
    // 2026-09-28 -- DUDAS.md "Gaps... NO implementados #3"): V1 (`ListadoAbonos.jsx`)
    // siempre traía ambos además del buscador de texto libre. El backend
    // (`abonos.service.js#listar`) ya soporta `?fechaDesde=&fechaHasta=&clientes=`.
    filtrosExtra: [
      { key: 'fecha', label: 'Fecha', type: 'date-range' },
      { key: 'clientes', label: 'Cliente', type: 'ref', refRoute: 'clientes', refLabel: 'nombre_comercial' },
    ],
    columns: [
      // "Numero" agregado (auditoría CxC 2026-09-28): V1 (`ListadoAbonos.jsx`)
      // siempre mostraba el folio `idAbono` como primera columna.
      { key: 'idAbono', label: 'Número', sortable: true, render: (r) => r.idAbono ?? '—' },
      { key: 'fecha', label: 'Fecha', sortable: true },
      { key: 'clientes', label: 'Cliente', sortable: true, render: (r) => r.clientes?.nombre_comercial || r.clientes?.[0]?.nombre_comercial || '—' },
      { key: 'formaPago', label: 'Forma de pago', sortable: true },
      { key: 'observaciones', label: 'Observaciones', sortable: true },
      { key: 'importe', label: 'Importe', sortable: true, className: 'text-right', render: (r) => money(r.importe) },
    ],
    // D-11.9 SUPERADA 2026-09-28 (instrucción explícita: "tienes que hacer todo
    // como V1"): la captura real de Abonos (conciliación multi-cargo, igual que
    // `AbonosCreate.jsx` de V1) se reconstruyó completa en
    // `pages/finanzas/AbonosCapturaPage.jsx` — ya NO se usa el alta simple genérica
    // de `ResourcePage` (`fields` se quita a propósito: este listado ahora es de
    // SOLO LECTURA + baja lógica; el botón "Nuevo" real vive en `AbonosPage.jsx`,
    // que enlaza a `/abonos/nuevo`).
  },

  devoluciones: {
    route: 'devoluciones', title: 'Devolución', recurso: 'devoluciones', sortBy: 'createdAt', exportable: true,
    // Filtros de Fecha Inicio/Fin y Cliente (segunda ronda de auditoría CxC,
    // 2026-09-28 -- DUDAS.md "Gaps... NO implementados #3"): V1
    // (`ListadoDevoluciones.jsx`) siempre traía ambos además del buscador de texto
    // libre. El backend (`devoluciones.service.js#listar`) ya soporta
    // `?fechaDesde=&fechaHasta=&clientes=`.
    filtrosExtra: [
      { key: 'fecha', label: 'Fecha', type: 'date-range' },
      { key: 'clientes', label: 'Cliente', type: 'ref', refRoute: 'clientes', refLabel: 'nombre_comercial' },
    ],
    columns: [
      // "Numero" agregado (auditoría CxC 2026-09-28): V1 (`ListadoDevoluciones.jsx`)
      // siempre mostraba el folio `idDevolucion` como primera columna.
      { key: 'idDevolucion', label: 'Número', sortable: true, render: (r) => r.idDevolucion ?? '—' },
      { key: 'fecha', label: 'Fecha', sortable: true },
      { key: 'clientes', label: 'Cliente', sortable: true, render: (r) => r.clientes?.nombre_comercial || r.clientes?.[0]?.nombre_comercial || '—' },
      // NOTA (re-auditoría CxC 2026-09-28, contra el tbody REAL de
      // `ListadoDevoluciones.jsx` línea por línea, no el array `headers`/`columns`):
      // V1 mapea `bancos`/`observaciones`/`concepto` al objeto de fila, pero el
      // `<tbody>` real SOLO renderiza `numero`/`fecha`/`clientes`/`importe` + el botón
      // de editar — "Concepto" NUNCA se muestra como columna en V1 (sí se captura en
      // el form de alta, pero no se lista). Columna eliminada aquí por no tener
      // respaldo en el tbody real (mismo tipo de regresión ya detectado y corregido
      // en Contenedores).
      { key: 'importe', label: 'Importe', sortable: true, className: 'text-right', render: (r) => money(r.importe) },
    ],
    fields: [
      { key: 'fecha', label: 'Fecha', type: 'date', required: true },
      { key: 'clientes', label: 'Cliente', type: 'ref', refRoute: 'clientes', refLabel: 'nombre_comercial', required: true },
      { key: 'importe', label: 'Importe', type: 'number', required: true },
      { key: 'concepto', label: 'Concepto', type: 'select', options: [
        { value: 'Bonificacion', label: 'Bonificación' },
        { value: 'Descuento', label: 'Descuento' },
        { value: 'Regreso', label: 'Regreso' },
      ] },
      { key: 'bancos', label: 'Banco (egreso)', type: 'ref', refRoute: 'bancos', refLabel: 'banco' },
      { key: 'observaciones', label: 'Observaciones', type: 'textarea' },
    ],
  },

  'devoluciones-inversa': {
    route: 'devoluciones-inversa', title: 'Devolución Inversa', recurso: 'devoluciones-inversa', sortBy: 'createdAt', exportable: true,
    // "Numero"/"Banco" agregados (auditoría CxC 2026-09-28): V1
    // (`ListadoDevolucionesInversa.jsx`) mostraba folio `idDevolucionInversa` y el
    // banco de egreso en la tabla — V2 solo los capturaba en el form.
    // Filtros de Fecha Inicio/Fin y Cliente (segunda ronda de auditoría CxC,
    // 2026-09-28 -- DUDAS.md "Gaps... NO implementados #3"): V1 también los traía
    // aquí. El backend (`devoluciones-inversa.service.js#listar`) ya soporta
    // `?fechaDesde=&fechaHasta=&clientes=`.
    filtrosExtra: [
      { key: 'fecha', label: 'Fecha', type: 'date-range' },
      { key: 'clientes', label: 'Cliente', type: 'ref', refRoute: 'clientes', refLabel: 'nombre_comercial' },
    ],
    columns: [
      { key: 'idDevolucionInversa', label: 'Número', sortable: true, render: (r) => r.idDevolucionInversa ?? '—' },
      { key: 'fecha', label: 'Fecha', sortable: true },
      { key: 'folio', label: 'Folio', sortable: true },
      { key: 'clientes', label: 'Cliente', sortable: true, render: (r) => r.clientes?.nombre_comercial || r.clientes?.[0]?.nombre_comercial || '—' },
      { key: 'concepto', label: 'Concepto', sortable: true },
      { key: 'bancos', label: 'Banco', sortable: true, render: (r) => r.bancos?.banco || r.bancos?.[0]?.banco || '—' },
      { key: 'importe', label: 'Importe', sortable: true, className: 'text-right', render: (r) => money(r.importe) },
    ],
    fields: [
      { key: 'fecha', label: 'Fecha', type: 'date', required: true },
      { key: 'folio', label: 'Folio', type: 'text' },
      { key: 'clientes', label: 'Cliente', type: 'ref', refRoute: 'clientes', refLabel: 'nombre_comercial', required: true },
      { key: 'importe', label: 'Importe', type: 'number', required: true },
      { key: 'concepto', label: 'Concepto', type: 'select', options: [
        { value: 'Bonificacion', label: 'Bonificación' },
        { value: 'Descuento', label: 'Descuento' },
        { value: 'Regreso', label: 'Regreso' },
        { value: 'Contenedor', label: 'Contenedor' },
      ] },
      // D-AUDIT-BANCOS: V1 (`DevolucionesInversaCreate.jsx`) filtra el <select> de
      // Banco a solo cuentas en MXN (`if(a.moneda == "MXN")`) — a diferencia de
      // Devoluciones (normal), que no filtra por moneda. Replicado aquí con
      // `refFilter` (ver FormModal.jsx) para no dejar elegir un banco en USD, que V1
      // nunca permitía desde esta pantalla.
      { key: 'bancos', label: 'Banco (egreso, solo MXN)', type: 'ref', refRoute: 'bancos', refLabel: 'banco', refFilter: (b) => b.moneda === 'MXN' },
      { key: 'observaciones', label: 'Observaciones', type: 'textarea' },
    ],
  },

  'bonificaciones-proveedores': {
    // D-11.4 CORREGIDA 2026-09-28: sí existe pantalla real de V1
    // (`IbuFront/.../CXP/BonificacionesProveedores/BonificacionesProveedoresCreate.jsx`)
    // — un agente anterior no la encontró. Confirmado: es en efecto un documento
    // simple (fecha + proveedor + importe + observaciones, sin banco ni
    // conciliación multi-renglón) — los campos ya definidos aquí coinciden 1:1 con
    // el formulario real de V1, no hace falta reconstruir nada.
    //
    // Columna "Numero" agregada (auditoría CxP 2026-09-28): V1
    // (`ListadoBonificacionesProveedores.jsx`) siempre mostraba el folio
    // `idBonificacionProveedor` como primera columna — el modelo V2 sí lo asigna
    // (`folio.service.js#SERIES.BONIFICACION_PROVEEDOR`, ver
    // `bonificaciones-proveedores.model.js`) pero la columna no se había replicado
    // aquí, mismo patrón que "Numero"/`idCargo` en Cargos/CargosContenedor arriba.
    route: 'bonificaciones-proveedores', title: 'Bonificación a Proveedor', recurso: 'bonificaciones-proveedores', sortBy: 'createdAt', exportable: true,
    columns: [
      { key: 'idBonificacionProveedor', label: 'Número', sortable: true, render: (r) => r.idBonificacionProveedor ?? '—' },
      { key: 'fecha', label: 'Fecha', sortable: true },
      { key: 'proveedores', label: 'Proveedor', sortable: true, render: (r) => r.proveedores?.name || r.proveedores?.[0]?.name || '—' },
      { key: 'observaciones', label: 'Observaciones', sortable: true },
      { key: 'importe', label: 'Importe', sortable: true, className: 'text-right', render: (r) => money(r.importe) },
    ],
    fields: [
      { key: 'fecha', label: 'Fecha', type: 'date', required: true },
      { key: 'proveedores', label: 'Proveedor', type: 'ref', refRoute: 'proveedores', refLabel: 'name', required: true },
      { key: 'importe', label: 'Importe', type: 'number', required: true },
      { key: 'observaciones', label: 'Observaciones', type: 'textarea', required: true },
    ],
  },

  bancos: {
    route: 'bancos', title: 'Banco', recurso: 'bancos', sortBy: 'banco', exportable: true,
    columns: [
      { key: 'banco', label: 'Banco', sortable: true },
      { key: 'cuenta', label: 'Cuenta' },
      { key: 'moneda', label: 'Moneda' },
      { key: 'saldo', label: 'Saldo', className: 'text-right', render: (r) => money(Number(r.abonos || 0) - Number(r.cargos || 0)) },
    ],
    fields: [
      { key: 'banco', label: 'Banco', type: 'text', required: true },
      { key: 'cuenta', label: 'Cuenta', type: 'text', required: true },
      { key: 'moneda', label: 'Moneda', type: 'select', options: MONEDA_OPTS, required: true, placeholder: 'Selecciona' },
    ],
    // Botón "Estado de Cuenta" por fila (Tarea 4, segunda ronda de auditoría CxC
    // 2026-09-28 -- DUDAS.md "Gaps... NO implementados #8"): V1 (`ListadoBancos.jsx`)
    // tenía este modal embebido con saldo corrido -- ver `EstadoCuentaBancoModal.jsx`.
    // V1 (`ListadoBancos.jsx`) por fila: Traspaso ($), Editar, Estado de Cuenta, Baja.
    extraRowActions: (row, { hasPermiso, recargar }) => (
      <>
        <TraspasoBancoButton row={row} hasPermiso={hasPermiso} recargar={recargar} />
        <EstadoCuentaBancoButton row={row} hasPermiso={hasPermiso} />
      </>
    ),
  },

  // NOTA (Tarea 3, segunda ronda de auditoría CxC 2026-09-28 -- DUDAS.md "Gaps... NO
  // implementados #7"): esta entrada YA NO SE USA -- `BancosTraspasosPage.jsx` dejó de
  // ser un `ResourcePage` genérico y ahora es bespoke (mismo motivo que
  // `cargos`/`cargos-contenedor` arriba), porque el `FormModal` genérico no soporta la
  // calculadora en vivo de importe-destino/comisión ni el filtro dinámico del
  // `<select>` de Banco Destino (excluir el banco origen elegido) que V1 sí tiene en
  // su modal de Traspaso (`ListadoBancos.jsx`). Se deja esta config sin borrar (por si
  // algún otro flujo la referenciara) pero no debería usarse para esa ruta.
  'bancos-traspasos': {
    // D-11.5: V1 no tiene listado dedicado de traspasos (vivía como modal dentro de
    // Bancos). El backend V2 sí expone CRUD completo bajo `/bancos-traspasos`, así
    // que se construye como página propia (ResourcePage genérico) en vez de un modal
    // incrustado en Bancos, para no tocar `resources.js`/Bancos existente.
    route: 'bancos-traspasos', title: 'Traspaso entre Bancos', recurso: 'bancos-traspasos', sortBy: 'createdAt', exportable: true,
    columns: [
      { key: 'fecha', label: 'Fecha', sortable: true },
      { key: 'bancoOrigen', label: 'Origen', render: (r) => r.bancoOrigen?.banco || r.bancoOrigen?.[0]?.banco || '—' },
      { key: 'bancoDestino', label: 'Destino', render: (r) => r.bancoDestino?.banco || r.bancoDestino?.[0]?.banco || '—' },
      { key: 'importe', label: 'Importe', className: 'text-right', render: (r) => money(r.importe) },
      { key: 'tipoCambio', label: 'T.C.' },
      { key: 'is_active', label: 'Estatus', render: (r) => (r.is_active === 'No' ? 'Cancelado' : 'Activo') },
    ],
    fields: [
      { key: 'fecha', label: 'Fecha', type: 'date', required: true },
      { key: 'bancoOrigen', label: 'Banco origen', type: 'ref', refRoute: 'bancos', refLabel: 'banco', required: true },
      { key: 'bancoDestino', label: 'Banco destino', type: 'ref', refRoute: 'bancos', refLabel: 'banco', required: true },
      { key: 'importe', label: 'Importe', type: 'number', required: true },
      { key: 'importeDestino', label: 'Importe destino (si cambia moneda)', type: 'number' },
      { key: 'monedaOrigen', label: 'Moneda origen', type: 'select', options: MONEDA_OPTS },
      { key: 'monedaDestino', label: 'Moneda destino', type: 'select', options: MONEDA_OPTS },
      { key: 'tipoCambio', label: 'Tipo de cambio', type: 'number' },
      { key: 'comisionBancoDestinoCantidad', label: 'Comisión $ (destino)', type: 'number' },
      { key: 'comisionBancoDestinoPorcentaje', label: 'Comisión % (destino)', type: 'number' },
      { key: 'observaciones', label: 'Observaciones', type: 'textarea' },
    ],
  },
};

export default RESOURCES_FINANZAS_CXC;
